import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import MemberRelationshipEditor from '../common/MemberRelationshipEditor';
import {
  dismissRelationshipNudge,
  fetchPendingRelationshipNudges,
  saveRelationshipWith,
} from '../../services/memberRelationshipService';

const RelationshipNudgePopup = () => {
  const { token } = useSelector((state) => state.auth);
  const [nudges, setNudges] = useState([]);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const current = nudges[0] || null;
  const firstName = useMemo(() => {
    const name = String(current?.memberName || 'this member').trim();
    return name.split(/\s+/)[0] || 'this member';
  }, [current]);

  const loadNudges = useCallback(async () => {
    if (!token) return;
    try {
      const list = await fetchPendingRelationshipNudges();
      setNudges(list);
      setAdding(false);
    } catch (err) {
      console.error('Failed to load relationship nudges:', err);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    loadNudges();
    const interval = setInterval(loadNudges, 60000);
    return () => clearInterval(interval);
  }, [token, loadNudges]);

  const advance = () => {
    setAdding(false);
    setNudges((prev) => prev.slice(1));
  };

  const handleDismiss = async () => {
    if (!current || dismissing) return;
    setDismissing(true);
    try {
      await dismissRelationshipNudge(current.memberUserId);
      advance();
    } catch (err) {
      console.error('Failed to dismiss relationship nudge:', err);
    } finally {
      setDismissing(false);
    }
  };

  const handleSave = async (body) => {
    if (!current) return;
    setSaving(true);
    try {
      await saveRelationshipWith(current.memberUserId, body);
      try {
        await dismissRelationshipNudge(current.memberUserId);
      } catch (err) {
        console.error('Failed to clear relationship nudge:', err);
      }
      advance();
    } finally {
      setSaving(false);
    }
  };

  if (!token || !current) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1980] flex items-center justify-center p-4 sm:p-6 announcement-popup-root">
      <div
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px] announcement-popup-backdrop"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-sky-400/30 via-indigo-300/20 to-fuchsia-400/30 announcement-popup-wash"
        aria-hidden="true"
      />

      <div
        className="relative w-full max-w-xl announcement-popup-card ring-2 ring-sky-400/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="relationship-nudge-title"
      >
        <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-white/40 via-transparent to-white/10 pointer-events-none" />
        <div className="relative rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
          <div className="h-2 w-full bg-gradient-to-r from-rose-400 via-amber-300 to-sky-400" />
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-5">
              <div className="announcement-popup-badge flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-400 via-amber-300 to-sky-400 flex items-center justify-center shadow-lg">
                <svg className="w-7 h-7 text-white drop-shadow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <span className="inline-flex px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200 mb-2">
                  Relationship
                </span>
                <h3
                  id="relationship-nudge-title"
                  className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight tracking-tight"
                >
                  Add a relationship with {firstName}
                </h3>
              </div>
            </div>

            {adding ? (
              <MemberRelationshipEditor
                memberName={current.memberName}
                relationship={null}
                loading={false}
                saving={saving}
                startInAdd
                onSave={handleSave}
                onDelete={() => {}}
                onCancel={() => setAdding(false)}
              />
            ) : (
              <>
                <p className="mb-7 text-gray-800 dark:text-gray-100 leading-7 text-base sm:text-lg font-medium">
                  Add a relationship with {firstName} so we can serve you better.
                </p>
                {current.lastNudge ? (
                  <p className="mb-7 -mt-4 text-sm text-gray-600 dark:text-gray-300">
                    This is the last reminder. If you skip, we will save this as Other.
                  </p>
                ) : null}
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleDismiss}
                    disabled={dismissing}
                    className="px-5 py-3 text-sm font-semibold text-gray-700 dark:text-gray-200 rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60"
                  >
                    {dismissing ? 'Saving…' : 'Maybe later'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className="announcement-ack-btn inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-bold text-white rounded-xl shadow-lg bg-sky-500 hover:bg-sky-600"
                  >
                    Add relationship
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RelationshipNudgePopup;
